import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ITextLabel } from '@fi-app/fi-translation/fi-translation';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { interval, Subject } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';
import { FiTranslationService } from '@fi-app/fi-translation/fi-translation.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { isDefined } from '@fi-shared/utils';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiApplicationLanguageLabelType } from '@fi-app/fi-translation/fi-application-language-label-type';

@Component({
    selector: 'fi-translation-item',
    templateUrl: './fi-translation-item.component.html',
    styleUrls: ['./fi-translation-item.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-translation-item' },
    standalone: false,
})
export class FiTranslationItemComponent implements OnInit, OnDestroy {
    @Input() textLabel: ITextLabel;
    @Input() allTextLabels: ITextLabel[];
    @Output() textLabelChange = new EventEmitter<ITextLabel>();
    @Input() applicationLanguages: INameIdObj[];
    @Input() selectedLanguageIds: number[];
    @Input() selected: boolean;
    @Input() fullWidthMode: boolean;
    @Output() textLabelDeleteClick = new EventEmitter<void>();

    @ViewChild(FiValidatingFormDirective) translationItemForm: FiValidatingFormDirective;
    private _dirty = false;
    private _textChanged$ = new Subject<void>();

    readonly availableLabelTypes: { name: string; value: FiApplicationLanguageLabelType }[] = [
        {
            name: 'Common',
            value: FiApplicationLanguageLabelType.Common,
        },
        {
            name: 'Page AI sample',
            value: FiApplicationLanguageLabelType.DocumentAISample,
        },
        {
            name: 'Checklist AI sample',
            value: FiApplicationLanguageLabelType.ChecklistV2AISample,
        },
    ];

    constructor(
        private cdr: ChangeDetectorRef,
        private translationService: FiTranslationService,
        private userDataService: FiUserDataService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    @HostBinding('class.new_label')
    get isNewLabel() {
        return !this.textLabel.TextId;
    }

    ngOnInit(): void {
        this._textChanged$
            .pipe(takeUntil(this.ngOnDestroy$))
            .pipe(debounce(() => interval(500)))
            .subscribe(() => {
                this.translationItemForm.updateValidity();
                this.cdr.markForCheck();
            });

        if (!this.textLabel.TextId) {
            for (const prop in this.textLabel.Values) {
                if (this.textLabel.Values[prop].Value) {
                    this._dirty = true;
                    break;
                }
            }
        }
    }

    save() {
        const saveTextLabelFn = () =>
            this.translationService.saveTextLabel(this.textLabel).then((newTextLabel) => {
                this._dirty = false;
                this.textLabelChange.next(newTextLabel);
            });

        const engTextValue = this.textLabel.Values[FiApplicationLanguageType.English]?.Value;
        const duplicateTextIds = this.allTextLabels
            .filter((existingTextLabel) => {
                const result =
                    !!engTextValue &&
                    this.textLabel.TextId !== existingTextLabel.TextId &&
                    existingTextLabel.Values[FiApplicationLanguageType.English]?.Value === engTextValue;
                return result;
            })
            .map((existingTextLabel) => existingTextLabel.TextId);

        if (duplicateTextIds.length) {
            this.fiDialogsService
                .confirm({
                    headerText: this.appTextService.get('common.warning'),
                    messageText: this.appTextService.get('translation.duplicates_found', {
                        '{textIds}': duplicateTextIds.join(','),
                    }),
                })
                .onYesButtonClick(() => {
                    saveTextLabelFn();
                });
        } else {
            saveTextLabelFn();
        }
    }

    onTextChanged() {
        this._dirty = true;
        this._textChanged$.next();
    }

    isSaveDisabled() {
        return !this._dirty || !!this.translationItemForm?.errors;
    }

    getLanguageName(applicationLanguageType: FiApplicationLanguageType) {
        return this.applicationLanguages.find((item) => item.Id == applicationLanguageType)?.Name;
    }

    isTextLabelValueShown(applicationLanguageType: FiApplicationLanguageType) {
        return this.selectedLanguageIds.indexOf(applicationLanguageType) !== -1;
    }

    deleteClick() {
        this.textLabelDeleteClick.next();
    }

    hasEditAccess() {
        return this.userDataService.userData.HasMasterIpAddress;
    }

    isTextLabelValueRequired() {
        for (let prop in this.textLabel.Values) {
            if (this.textLabel.Values[prop].Value) {
                return false;
            }
        }
        return true;
    }

    getChangedOn(textLabel: ITextLabel) {
        if (isDefined(textLabel.ChangedOn)) {
            return this.fiFormatDateService.formatDate(textLabel.ChangedOn, 'date-time', { showToday: false });
        }
        return '';
    }

    getChangedByUserName(textLabel: ITextLabel) {
        if (isDefined(textLabel.ChangedByUserName)) {
            return `${this.appTextService.get('by').toLowerCase()} ${textLabel.ChangedByUserName}`;
        }
        return '';
    }

    getUsageText(textLabel: ITextLabel) {
        const parts: string[] = [];
        if (textLabel.IsWeb) {
            parts.push('Web');
        }
        if (textLabel.IsFlutter) {
            parts.push('Flutter');
        }
        if (textLabel.IsBackend) {
            parts.push('Backend');
        }
        if (!parts.length) {
            parts.push('NONE');
        }
        return parts.join(', ');
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
